import React, { useEffect, useRef } from 'react';
import MainAppLayout, { MainAppLayoutProps } from 'components/templates/MainAppLayout/MainAppLayout';
import useAppointmentLayoutProps from 'hooks/useAppointmentLayoutProps';
import { useNavigation } from 'hooks/useNavigation';
import { RouteKeys } from 'models/RouteModels';
import { useNavigate } from 'react-router-dom';
import { routesMap } from 'data/routes';
import { containerStyles } from 'helpers/helpers';
import { FullJoints, Xray } from 'models/XrayModels';
import { Patient } from 'models/PatientModels';
import { PRO } from 'models/SurveyModels';
import MDButton from 'components/atoms/MDButton/MDButton';
import { Breakpoints, MUIColors, Shadows } from 'models/StyleModels';
import MDBox from 'components/atoms/MDBox/MDBox';
import PDF from 'components/molecules/PDF/PDF';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import colors from 'assets/theme/base/colors';
import { useBreakpoints } from 'hooks/useBreakpoints';
import MDTypography from 'components/atoms/MDTypography/MDTypography';
import { Appointment } from 'models/AppointmentModels';
import { Org } from 'models/OrgModels';
import DemoFlowLayout from 'components/templates/DemoFlowLayout/DemoFlowLayout';

interface ReportPageProps {
  org: Org | undefined,
  patient?: Patient | null,
  appointment?: Appointment | null,
  prosByJoints?: { [FJ in FullJoints]?: PRO },
  xraysByJoints?: { [FJ in FullJoints]?: Xray[] },
  generatePdf: (
    org: Org | undefined,
    patient?: Patient | null,
    appointment?: Appointment | null,
    prosByJoints?: { [FJ in FullJoints]?: PRO },
    xraysByJoints?: { [FJ in FullJoints]?: Xray[] },
  ) => Promise<void>,
  flow: string | null,
  isLoading: boolean,
  pdfError: boolean,
  pdfLoading: boolean,
  pdfFile: Blob | null
}

export const ReportPageContent: React.FC<ReportPageProps> = (props: ReportPageProps) => {

  const {
    org,
    appointment,
    patient,
    prosByJoints,
    xraysByJoints,
    generatePdf,
    flow,
    isLoading,
    pdfFile,
    pdfLoading,
    pdfError
  } = props

  const { breakpointBreached } = useBreakpoints({ breakpoint: Breakpoints.MEDIUM });

  const navigate = useNavigate();

  const { navigateToBaseRouteWithAptmtDetails } = useNavigation();

  const { headerItems } = useAppointmentLayoutProps({
    appointment,
    patient: patient,
    pageTitle: 'Care Report',
    disabledNav: flow === "DEMO"
  });

  const handleNext = flow === "DEMO" ? () => navigate(`${routesMap[RouteKeys.DEMO_EXAM_ROOM].route}/?patientName=${patient?.first_name.toLowerCase()}_${patient?.last_name.toLowerCase()}`) : () => navigate(routesMap[RouteKeys.TRIAGE_LIST].route);
  const handlePrevious = flow === "DEMO" ? () => navigate(`${routesMap[RouteKeys.DEMO_EXAM_ROOM].route}/?patientName=${patient?.first_name.toLowerCase()}_${patient?.last_name.toLowerCase()}`) :
    () => appointment
      ? navigateToBaseRouteWithAptmtDetails({
        routeKey: RouteKeys.EXAM_ROOM_ALL_JOINTS,
        appointmentId: appointment.appointment_id,
        appointmentDateISOString: appointment.appointment_date,
      })
      : {};

  // opens up the PDF in the browser's native PDF viewer
  const handlePrintPdf = (blob: Blob | null) => {
    if (!blob) return;
    const url = window.URL.createObjectURL(blob);
    const newWindow = window.open(url, '_blank');
    if (newWindow) newWindow.focus();
    window.URL.revokeObjectURL(url);
  };

  // creates a dummy download link on the DOM and clicks it
  // to initiate the PDF download
  const handleDownloadPdf = (
    blob: Blob | null,
    patient?: Patient | null,
  ) => {
    if (!blob || !patient) return;
    const downloadLink = document.createElement('a');
    const url = URL.createObjectURL(blob);
    downloadLink.href = url;
    downloadLink.download = `patient_report-${patient?.last_name}-${new Date().toISOString()}.pdf`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(url);
  };

  const mainAppLayoutProps: MainAppLayoutProps = {
    headerItems,
    navActions: {
      previous: {
        text: 'Back',
        action: handleNext,
      },
      next: {
        text: 'Complete Evaluation',
        action: handlePrevious,
      },
    },
  };

  const effectSuccessRef = useRef<boolean>(false);

  // this effect fires off the initial request to the POST/pdf endpoint in order
  // to dynamically generate the PDF, once all of the required data has been fetched
  useEffect(() => {
    if (
      isLoading
      || !appointment
      || !appointment.joints
      || !xraysByJoints
      || !prosByJoints
      || pdfFile
      || effectSuccessRef.current
    ) return;

    // prevent the request from being made multiple times due to rerenders
    effectSuccessRef.current = true;

    generatePdf(org, patient, appointment, prosByJoints, xraysByJoints);

    // eslint-disable-next-line
  }, [
    isLoading,
    org,
    appointment,
    xraysByJoints,
    prosByJoints,
    effectSuccessRef,
  ]);

  const generatePdfPlaceholder = (mode: 'loading' | 'error') => (
    <MDBox
      shadow={Shadows.MEDIUM}
      {...containerStyles({
        display: 'flex',
        flexDirection: 'column',
        height: breakpointBreached ? '55vh' : '100%',
        width: breakpointBreached ? '100%' : '70%',
        maxWidth: '27rem',
        minWidth: '20rem',
        justifyContent: 'space-around',
        alignItems: 'center',
        position: 'relative',
        borderRadius: '8px',
        backgroundColor: colors.grey[100],
      })}
    >
      <div
        style={{
          position: 'absolute',
          zIndex: 2,
          width: '100%',
          height: '100%',
          textAlign: 'center',
          top: 0,
          left: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <MDTypography color={mode === 'loading' ? MUIColors.PRIMARY : MUIColors.ERROR} textAlign='center' mb={2} variant='h3'>
          {
            mode === 'loading'
              ? <>Please wait while <br /> we generate your report.</>
              : <>There was a problem <br /> generating your report.</>
          }
        </MDTypography>
        {
          mode === 'loading'
            ? <FontAwesomeIcon icon={faCircleNotch} color={colors.primary.main} spin size="2x" />
            : (
              <MDButton
                variant='contained'
                color={MUIColors.ERROR}
                onClick={() => generatePdf(org, patient, appointment, prosByJoints, xraysByJoints)}
              >
                Retry
              </MDButton>
            )
        }
      </div>
      <div className='ExamReportPage__LoaderBoxLg' />
      <div style={{ width: '100%', height: '20%', display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
        {Array.from(new Array(3), (el, index) => (
          <div
            key={`box_${index + 1}`}
            className='ExamReportPage__LoaderBoxSm'
          />
        ))}
      </div>
      <div style={{ width: '100%', height: '20%', display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
        {Array.from(new Array(2), (el, index) => (
          <div
            key={`box_${index + 1}`}
            className='ExamReportPage__LoaderBoxMd'
          />
        ))}
      </div>
    </MDBox>
  );


  const reportPageContent = (
    <>
      <MDBox {...containerStyles({
        display: 'flex',
        flexDirection: breakpointBreached ? 'column' : 'row',
        height: breakpointBreached ? 'auto' : '55vh',
        width: '100%',
      })}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: breakpointBreached ? '100%' : '60%',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: breakpointBreached ? '1rem' : 0,
        }}>
          {(isLoading || pdfError || !pdfFile) && generatePdfPlaceholder(pdfError ? 'error' : 'loading')}
          {pdfFile && <PDF src={pdfFile} fadeOutBottom />}
        </div>
        <MDBox {...containerStyles({
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: breakpointBreached ? '100%' : '40%',
          justifyContent: 'center',
          alignItems: 'center',
        })}>
          <MDButton
            disabled={pdfLoading || !pdfFile}
            color={MUIColors.SECONDARY}
            variant='contained'
            style={{ marginBottom: '1rem' }}
            onClick={() => handleDownloadPdf(pdfFile, patient)}
          >
            Download Report
          </MDButton>
          <MDButton
            disabled={pdfLoading || !pdfFile}
            color={MUIColors.SECONDARY}
            variant='contained'
            onClick={() => handlePrintPdf(pdfFile)}
          >
            Print Report
          </MDButton>
        </MDBox>
      </MDBox>
    </>
  );

  if (flow === "DEMO") {
    return (
      <DemoFlowLayout {...mainAppLayoutProps} title="Exam Room" singlePatient={patient}>
        {reportPageContent}
      </DemoFlowLayout>)
  }
  else return (
    <MainAppLayout {...mainAppLayoutProps}>
      {reportPageContent}
    </MainAppLayout>
  )

}